/**
 * Room Configuration for booking-module
 * Provides room metadata without requiring DOM access
 * This replaces the old booking-engine.php DOM-based approach
 */

import { ChaletNameEnum } from "./availability.model";

export interface RoomInfo {
    abbreviation: string;
    name: string;
}

/**
 * Room configurations indexed by chalet name and room index
 * Based on the old booking-engine.php data-room-abbv-name attributes
 */
const ROOM_CONFIGS: { [chaletName: string]: RoomInfo[] } = {
    [ChaletNameEnum.PetitePia]: [
        { abbreviation: 'Rm 1', name: 'Bedroom 1' },
        { abbreviation: 'Rm 2', name: 'Bedroom 2' },
        { abbreviation: 'Rm 3', name: 'Bedroom 3' },
        { abbreviation: 'Rm 4', name: 'Bedroom 4' },
        { abbreviation: 'Apt', name: 'Apartment' }
    ],
    [ChaletNameEnum.PiaChaletPlusApt]: [
        { abbreviation: 'Rm 1', name: 'Bedroom 1' },
        { abbreviation: 'Rm 2', name: 'Bedroom 2' },
        { abbreviation: 'Rm 3', name: 'Bedroom 3' },
        { abbreviation: 'Rm 4', name: 'Bedroom 4' },
        { abbreviation: 'Rm 5', name: 'Bedroom 5 (Apartment)' },
        { abbreviation: 'Rm 6', name: 'Bedroom 6 (Apartment)' },
        { abbreviation: 'Rm 7', name: 'Bedroom 7 (Apartment)' }
    ],
    [ChaletNameEnum.LesLoups]: [
        { abbreviation: 'Rm 1', name: 'Bedroom 1' },
        { abbreviation: 'Rm 2', name: 'Bedroom 2' },
        { abbreviation: 'Rm 3', name: 'Bedroom 3' },
        { abbreviation: 'Rm 4', name: 'Bedroom 4' },
        { abbreviation: 'Rm 5', name: 'Bedroom 5' }
    ],
    [ChaletNameEnum.Galta]: [
        { abbreviation: 'Rm 1', name: 'Bedroom 1' },
        { abbreviation: 'Rm 2', name: 'Bedroom 2' },
        { abbreviation: 'Rm 3', name: 'Bedroom 3' },
        { abbreviation: 'Rm 4', name: 'Bedroom 4' },
        { abbreviation: 'Rm 5', name: 'Bedroom 5' }
    ],
    [ChaletNameEnum.Alya]: [
        { abbreviation: 'Rm 1', name: 'Bedroom 1' },
        { abbreviation: 'Rm 2', name: 'Bedroom 2' },
        { abbreviation: 'Rm 3', name: 'Bedroom 3' },
        { abbreviation: 'Rm 4', name: 'Bedroom 4' },
        { abbreviation: 'Rm 5', name: 'Bedroom 5' }
    ],
    [ChaletNameEnum.LesSolans]: [
        { abbreviation: 'Rm 1', name: 'Bedroom 1' },
        { abbreviation: 'Rm 2', name: 'Bedroom 2' },
        { abbreviation: 'Rm 3', name: 'Bedroom 3' },
        { abbreviation: 'Rm 4', name: 'Bedroom 4' },
        { abbreviation: 'Rm 5', name: 'Bedroom 5' },
        { abbreviation: 'Rm 6', name: 'Bedroom 6' },
        { abbreviation: 'Rm 7', name: 'Bedroom 7' }
    ],
    [ChaletNameEnum.EtoileDesNeiges]: [
        { abbreviation: 'Rm 1', name: 'Bedroom 1' },
        { abbreviation: 'Rm 2', name: 'Bedroom 2' },
        { abbreviation: 'Rm 3', name: 'Bedroom 3' },
        { abbreviation: 'Rm 4', name: 'Bedroom 4' },
        { abbreviation: 'Rm 5', name: 'Bedroom 5' },
        { abbreviation: 'Rm 6', name: 'Bedroom 6 (Children\'s room)' }
    ]
};

/**
 * Get room information by chalet name and room index
 * Returns a fallback if chalet/room not found
 */
export function getRoomInfo(roomIndex: number, chaletName: ChaletNameEnum | string): RoomInfo {
    const rooms = ROOM_CONFIGS[chaletName];
    
    if (!rooms || !rooms[roomIndex]) {
        // Fallback for unknown chalets or rooms
        return {
            abbreviation: `Rm ${roomIndex + 1}`,
            name: `Bedroom ${roomIndex + 1}`
        };
    }
    
    return rooms[roomIndex];
}
